import { BaseApiResponse } from '@core/swagger-client';
import { Button, Dialog, ErrorMessage, Loader, Select, Switch, Upload } from '@core/ui';
import { Trans, useTranslation } from 'react-i18next';
import type { useSpreadsheetImport } from '../../../hooks';

interface Props {
    /** The value returned from {@link useSpreadsheetImport}. */
    importer: ReturnType<typeof useSpreadsheetImport>;
    /** Whether a submit/import is currently in progress (drives the spinners). */
    isImporting: boolean;
    /** Called when the user presses the import button. */
    onImport: () => void;
    /** Label for the import button. */
    importButtonText: string;
    /** Whether the chunked-upload progress dialog is open. */
    progressOpen?: boolean;
    /** Current number of imported rows (for the progress dialog). */
    progressCurrent?: number;
    /** Total number of rows to import (for the progress dialog). */
    progressTotal?: number;
    /** Optional API error to surface above the dropzone. */
    error?: BaseApiResponse | null;
    /** Optional data-testid for the import button. */
    importTestId?: string;
}

export const SpreadsheetImport = ({
    importer,
    isImporting,
    onImport,
    importButtonText,
    progressOpen = false,
    progressCurrent = 0,
    progressTotal = 0,
    error,
    importTestId
}: Props) => {
    const { t } = useTranslation();

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        rejectionCode,
        workBookError,
        workSheetNames,
        selectedWorkSheet,
        setSelectedWorkSheet,
        hasHeader,
        setHasHeader,
        headers,
        mapping,
        setMapping,
        data,
        fields
    } = importer;

    return (
        <div className="max-w-4xl">
            <Dialog open={progressOpen} title={t('ImportInvites.Dialog.Title')}>
                <div className="flex flex-col gap-4 items-center">
                    <Loader />
                    {progressTotal > 0 && <p>{t('ImportInvites.Dialog.Progress', { current: progressCurrent, total: progressTotal })}</p>}
                </div>
            </Dialog>

            {error != null && <ErrorMessage apiError={error} classNames="mb-4" />}


            <div
                {...getRootProps()}
                className={`group relative w-full min-h-64 ${isDragActive && 'bg-accent-lighter'} hover:bg-accent-lighter focus-visible:bg-accent-lighter border-2 border-dashed ${!isDragActive && 'border-gray-500'} ${isDragActive && 'border-accent'} hover:border-accent focus-visible:border-accent rounded-lg flex mb-4 md:mb-6 outline-hidden overflow-hidden p-6 transition-colors duration-150`}
                data-testid="spreadsheet-import-dropzone"
            >
                <div className="flex flex-col items-center m-auto">
                    <Upload classNames="size-12 mb-1 stroke-current group-hover:stroke-primary transition-color duration-200 ease-in-out" />
                    <p className="font-accent font-medium text-3xl mb-2">{isDragActive ? t('Generic.DragDrop.Active') : t('Generic.DragDrop')}</p>
                    <p>
                        <Trans components={{ span: <span className="border-b-2 border-primary" /> }} i18nKey="Generic.DragDrop.BrowseFiles" />
                    </p>
                    <p className="mt-4">
                        {t('Generic.DragDrop.AcceptedText')}
                        <span className="font-medium">{t('ImportInvites.DragDrop.AcceptedFileTypes')}</span>
                    </p>
                    {rejectionCode && <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />}
                </div>
                <input {...getInputProps()} />
            </div>

            <p>{t('ImportInvites.Text.HasHeader')}</p>
            <Switch
                classNames="mt-2 mb-10"
                id="hasHeader"
                checked={hasHeader}
                onChange={() => setHasHeader(!hasHeader)}
                label={t('ImportInvites.Label.HasHeader')}
            />

            {workSheetNames.length > 1 && (
                <>
                    <p>{t('ImportInvites.Text.MultipleWorksheets')}</p>
                    <Select
                        id="worksheet"
                        label={t('ImportInvites.Label.Worksheet')}
                        value={selectedWorkSheet || ''}
                        onChange={(event) => setSelectedWorkSheet(event.target.value)}
                        classNames="max-w-80 mt-4 mb-10"
                    >
                        {workSheetNames.map((sheetName) => (
                            <option key={sheetName} value={sheetName}>
                                {sheetName}
                            </option>
                        ))}
                    </Select>
                </>
            )}

            {/* Always surface a parse error, and tell the user when a file was read but produced no
                rows — the common cause being a header-only / single-row file with "Document has a
                header" enabled (it consumes the only row). Without this the page just shows nothing. */}
            {workBookError && <ErrorMessage statusMessage={workBookError} classNames="mb-4" />}

            {!workBookError && workSheetNames.length > 0 && data.length === 0 && (
                <div data-testid="spreadsheet-import-no-data">
                    <ErrorMessage statusMessage={t('ImportInvites.Text.NoDataRows')} classNames="mb-4" />
                </div>
            )}

            {data && data.length > 0 && (
                <>
                    <p className="mb-4">{t('ImportInvites.Text.MapColumns')}</p>
                    <Button data-testid={importTestId} isLoading={isImporting} onClick={onImport} variant="primary">
                        {importButtonText}
                    </Button>
                    <div className={`bg-white ${workBookError ? 'border-2 border-warning ' : ''}rounded-lg shadow-md table-responsive my-4`}>
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    {headers.map((header, index) => (
                                        <th key={index}>
                                            {!mapping[index] && <span>{header}</span>}
                                            <select
                                                id={`mapping-${index}`}
                                                value={mapping[index] ?? ''}
                                                onChange={(event) => setMapping([...mapping.slice(0, index), event.target.value, ...mapping.slice(index + 1)])}
                                                className="ms-2"
                                            >
                                                <option value="">{t('ImportInvites.MapOption.NotImported')}</option>
                                                {fields.map((field) => (
                                                    <option key={field.name} value={field.name}>
                                                        {field.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {row.map((cell, cellIndex) => (
                                            <td key={cellIndex}>{cell}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Button isLoading={isImporting} onClick={onImport} variant="primary">
                        {importButtonText}
                    </Button>
                </>
            )}
        </div>
    );
};
